import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import { setPublicGallery } from "../../../redux/actions/events";
import EventsService from "../../../services/eventsService";
import { GALLERY_LOCALES, publicGalleryUrl } from "../../../helpers/publicUploads";
import { useGalleryLinkLocale } from "../../../helpers/galleryLinkLocale";
import { hasNoBalance, isPublicGalleryOn, publishEventWarnings } from "../../../helpers/eventPublication";
import { ownsEvent } from "../../../helpers/eventSponsors";
import { ERROR_TOAST_CONFIG, formatCount } from "../../../helpers/functions";
import { onPhotosChanged } from "../../../helpers/photosChanged";
import { SponsorsEditor } from "./eventSponsors";
import PhotoUploader from "./photoUploader";
import { RevAlert, RevButton, RevCard, RevChip, RevProgress, RevSegmented, RevSwitch } from "../../../components/ds";

const TOAST = { ...ERROR_TOAST_CONFIG, autoClose: 4000 };
const CAPACITY = 250;
const LINK_LOCALES = ['', ...GALLERY_LOCALES];

const GlobeIcon = () => (
    <svg className="rev-pubgal__globe" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
        <path d="M2 12h20" />
    </svg>
);

/**
 * Pestaña «Galería pública»: todo lo que solo existe en gallery.revopic.com/g/{CODE}.
 * El interruptor es «Publicar evento» (galería pública visible y evento en el listado de la
 * app), independiente de «Terminar subida y activar selfies» (cabecera). Solo el dueño la ve (eventAside) y la gobierna. Las fotos
 * públicas se suben aquí (también como fotos normales del evento), siempre, aunque los selfies
 * estén activos; se ven y se quitan una a una en Fotos.
 */
const EventPublicGallery = ({ eventId, selectedEvent, user, saving, setPublicGallery }) => {
    const { t } = useTranslation();
    const event = selectedEvent?.id === eventId ? selectedEvent : undefined;
    const [switchError, setSwitchError] = useState(null);
    const [qr, setQr] = useState(null);
    const [publicCount, setPublicCount] = useState(undefined);
    // Cada subida o publicación terminada vuelve a pedir el recuento.
    const [countVersion, setCountVersion] = useState(0);

    // '' = sin idioma en el enlace: cada visitante ve la galería en el suyo. La cabecera lo sigue.
    const [linkLocale, setLinkLocale] = useGalleryLinkLocale(eventId);
    const url = publicGalleryUrl(event?.code, linkLocale);
    const on = isPublicGalleryOn(event);
    // Publicado pero fuera del listado de la app por falta de saldo, como el chip de la cabecera.
    const hiddenForBalance = on && hasNoBalance(user);
    const locale = t('locale', { returnObjects: true }) || 'es-ES';

    useEffect(() => {
        if (!url) { setQr(null); return undefined; }
        let alive = true;
        QRCode.toDataURL(url, { width: 512, margin: 1 })
            .then(data => { if (alive) setQr(data); })
            .catch(() => { if (alive) setQr(null); });
        return () => { alive = false; };
    }, [url]);

    // El cupo lo lleva el backend; el evento en Redux puede venir de antes de la última subida.
    useEffect(() => {
        if (!event?.id) return undefined;
        return onPhotosChanged(event.id, () => setCountVersion(v => v + 1));
    }, [event?.id]);

    useEffect(() => {
        if (!event?.id || !ownsEvent(event, user)) return undefined;
        let alive = true;
        EventsService.publicUploads(event.id)
            .then(response => {
                const data = response.data?.Data || response.data || {};
                if (alive && typeof data.publicPhotoCount === 'number') setPublicCount(data.publicPhotoCount);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [event?.id, user?.id, countVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    // Al cambiar de evento no se enseña el recuento del anterior.
    useEffect(() => { setPublicCount(undefined); }, [event?.id]);

    if (!event || !user?.id) return <RevProgress aria-label={t('publicGallery.loading')} />;
    if (!ownsEvent(event, user)) return <RevAlert tone="warn" role="alert">{t('publicGallery.ownerOnly')}</RevAlert>;

    const toggle = async () => {
        if (saving) return;
        setSwitchError(null);
        try {
            const active = !on;
            const result = await setPublicGallery(event.id, active);
            toast.success(t(active
                ? (hasNoBalance(user) ? 'publicGallery.status.toast.onHidden' : 'publicGallery.status.toast.on')
                : 'publicGallery.status.toast.off', { code: event.code }), TOAST);
            publishEventWarnings({ result, active, publicPhotoCount: publicCount ?? event.publicPhotoCount })
                .forEach(key => toast.warning(t(key), TOAST));
        } catch (error) {
            const status = error?.response?.status;
            setSwitchError(status === 403 ? 'forbidden' : status === 404 ? 'notFound' : 'generic');
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success(t('publicGallery.linkCopied'), TOAST);
        } catch {
            toast.error(t('publicGallery.copyError'), TOAST);
        }
    };

    const count = publicCount ?? event.publicPhotoCount;
    const countKnown = typeof count === 'number';

    return (
        <div className="rev-pubgal">
            <RevCard title={t('publicGallery.status.title')}>
                <div className="rev-pubgal__estado">
                    <div className="rev-pubgal__estado-main">
                        <div className="rev-pubgal__estado-fila">
                            <RevSwitch as="button" bare role="switch" aria-checked={on} on={on}
                                aria-label={t('publicGallery.status.switch')} disabled={saving} onClick={toggle} />
                            <RevChip tone={on ? 'ok' : undefined}>{t(on ? 'publicGallery.status.on' : 'publicGallery.status.off')}</RevChip>
                            {saving && <span className="rev-help" role="status">{t('publicGallery.status.saving')}</span>}
                        </div>
                        {on ? <>
                            <ul className="rev-pubgal__efectos">
                                <li className="rev-pubgal__efecto rev-pubgal__efecto--ok">
                                    <span className="material-icons rev-icon-sm" aria-hidden="true">check_circle</span>
                                    {t('publicGallery.status.live.web')}
                                </li>
                                {hiddenForBalance
                                    ? <li className="rev-pubgal__efecto rev-pubgal__efecto--warn">
                                        <span className="material-icons rev-icon-sm" aria-hidden="true">warning</span>
                                        <span>
                                            {t('publicGallery.status.live.appHidden')}{' '}
                                            <Link to="/account/credit/home">{t('publicGallery.status.live.recharge')}</Link>
                                        </span>
                                    </li>
                                    : <li className="rev-pubgal__efecto rev-pubgal__efecto--ok">
                                        <span className="material-icons rev-icon-sm" aria-hidden="true">check_circle</span>
                                        {t('publicGallery.status.live.app')}
                                    </li>}
                            </ul>
                            <span className="rev-help">{t('publicGallery.status.helpOn', { code: event.code })}</span>
                        </> : <>
                            <span className="rev-help">{t('publicGallery.status.helpOff', { code: event.code })}</span>
                            <span className="rev-help">{t('publicGallery.status.benefitsTitle')}</span>
                            <ul className="rev-pubgal__efectos">
                                <li className="rev-pubgal__efecto">
                                    <span className="material-icons rev-icon-sm" aria-hidden="true">language</span>
                                    {t('publicGallery.status.benefit.web')}
                                </li>
                                <li className="rev-pubgal__efecto">
                                    <span className="material-icons rev-icon-sm" aria-hidden="true">smartphone</span>
                                    {t('publicGallery.status.benefit.app')}
                                </li>
                            </ul>
                            <span className="rev-help">{t('publicGallery.status.undo')}</span>
                        </>}
                        {switchError && <RevAlert tone="err" role="alert">{t(`publicGallery.status.error.${switchError}`)}</RevAlert>}
                    </div>
                    <div className="rev-pubgal__contador">
                        <span className="rev-pubgal__contador-num rev-num">{countKnown ? formatCount(count, locale) : '—'}</span>
                        <span className="rev-help">
                            {countKnown ? t('publicGallery.photos.count', { count }) : t('publicGallery.capacityUnknown')}
                            {' · '}
                            {t('publicGallery.photos.ofCapacity', { capacity: CAPACITY })}
                        </span>
                        <div className="rev-pubgal__acciones">
                            <RevButton as={Link} to={`/events/${event.id}/gallery`} variant="secondary" size="sm">{t('publicGallery.photos.manage')}</RevButton>
                        </div>
                    </div>
                </div>
            </RevCard>

            <PhotoUploader key={event.id} eventId={event.id} user={user} toPublic
                title={t('publicGallery.upload.title')} sub={t('publicGallery.upload.sub')} />

            <RevCard title={t('publicGallery.link.title')} sub={t('publicGallery.link.sub')}>
                {url ? (
                    <div className="rev-pubgal__enlace">
                        <div className="rev-pubgal__enlace-main">
                            <div className="rev-pubgal__idioma">
                                <RevSegmented role="group" aria-label={t('publicGallery.link.language')}
                                    items={LINK_LOCALES.map(value => ({
                                        value,
                                        label: value
                                            ? <><span aria-hidden="true">{value.toUpperCase()}</span><span className="rev-sr-only">{t(`publicGallery.link.languages.${value}`)}</span></>
                                            : <><GlobeIcon /><span className="rev-sr-only">{t('publicGallery.link.languageVisitor')}</span></>,
                                    }))}
                                    value={linkLocale} onChange={setLinkLocale} />
                                <span className="rev-help">{linkLocale
                                    ? t('publicGallery.link.languageFixedHelp', { language: t(`publicGallery.link.languages.${linkLocale}`) })
                                    : t('publicGallery.link.languageVisitorHelp')}</span>
                            </div>
                            <code className="rev-pubgal__url">{url}</code>
                            <div className="rev-pubgal__link-row">
                                <RevButton variant="secondary" onClick={copy}>{t('publicGallery.link.copy')}</RevButton>
                                <RevButton as="a" variant="secondary" href={url} target="_blank" rel="noopener noreferrer">{t('publicGallery.open')}</RevButton>
                            </div>
                            {!on && <span className="rev-help rev-help--warn">{t('publicGallery.link.offNote')}</span>}
                        </div>
                        <div className="rev-pubgal__qr">
                            {qr
                                ? <img className="rev-pubgal__qr-img" src={qr} alt={t('publicGallery.link.qrAlt', { code: event.code })} />
                                : <span className="rev-pubgal__qr-img rev-skel" aria-hidden="true" />}
                            <div className="rev-pubgal__qr-info">
                                <span className="rev-help">{t('publicGallery.link.qrHelp')}</span>
                                {qr && <RevButton as="a" variant="secondary" size="sm" fit href={qr} download={`galeria-${event.code}${linkLocale ? `-${linkLocale}` : ''}.png`}>
                                    {t('publicGallery.link.downloadQr')}
                                </RevButton>}
                            </div>
                        </div>
                    </div>
                ) : (
                    <RevAlert tone="warn" role="alert" data-pendiente="ds-enlace-galeria">{t('publicGallery.link.unconfigured')}</RevAlert>
                )}
            </RevCard>

            <SponsorsEditor key={event.id} eventId={event.id} />
        </div>
    );
};

const mapStateToProps = state => ({
    saving: state.eventsReducer.loadingPublicGallery,
});

const mapDispatchToProps = { setPublicGallery };

export default connect(mapStateToProps, mapDispatchToProps)(EventPublicGallery);
